import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";

import type { HomeFeedPage, PostResponse } from "../types/generated";

interface ConfirmedHomeQueryPins {
  query: Query;
  posts: Map<string, PostResponse>;
}

interface ConfirmedHomePinState {
  queries: Map<string, ConfirmedHomeQueryPins>;
  unsubscribe: () => void;
}

const confirmedHomePinStates = new WeakMap<
  QueryClient,
  ConfirmedHomePinState
>();

export function rewriteHomePages(
  data: InfiniteData<HomeFeedPage> | undefined,
  rewrite: (post: PostResponse) => PostResponse | null,
): InfiniteData<HomeFeedPage> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.flatMap<HomeFeedPage["items"][number]>((item) => {
        if (item.kind !== "post") return [item];
        const next = rewrite(item.post);
        return next ? [{ ...item, post: next }] : [];
      }),
    })),
  };
}

export function mergePostIntoHomePages(
  data: InfiniteData<HomeFeedPage> | undefined,
  post: PostResponse,
): InfiniteData<HomeFeedPage> | undefined {
  if (!data?.pages.length) return data;
  const withoutDuplicate = rewriteHomePages(data, (candidate) =>
    candidate.post_id === post.post_id ? null : candidate,
  );
  if (!withoutDuplicate?.pages.length) return withoutDuplicate;
  const [first, ...rest] = withoutDuplicate.pages;
  return {
    ...withoutDuplicate,
    pages: [
      {
        ...first,
        items: [
          { kind: "post", occurred_at: post.created_at, post },
          ...first.items,
        ],
      },
      ...rest,
    ],
  };
}

function homeContainsPost(
  data: InfiniteData<HomeFeedPage> | undefined,
  postId: string,
): boolean {
  return (
    data?.pages.some((page) =>
      page.items.some(
        (item) => item.kind === "post" && item.post.post_id === postId,
      ),
    ) ?? false
  );
}

function ensureHomePinState(queryClient: QueryClient): ConfirmedHomePinState {
  const existing = confirmedHomePinStates.get(queryClient);
  if (existing) return existing;
  const state: ConfirmedHomePinState = {
    queries: new Map(),
    unsubscribe: () => undefined,
  };
  state.unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    const pinned = state.queries.get(event.query.queryHash);
    if (!pinned || pinned.query !== event.query) return;
    if (event.type === "removed") {
      state.queries.delete(event.query.queryHash);
      if (state.queries.size === 0) {
        state.unsubscribe();
        confirmedHomePinStates.delete(queryClient);
      }
      return;
    }
    if (
      event.type !== "updated" ||
      event.action.type !== "success" ||
      event.action.manual
    ) {
      return;
    }
    let data = event.query.state.data as InfiniteData<HomeFeedPage> | undefined;
    let repaired = false;
    for (const [postId, post] of pinned.posts) {
      if (homeContainsPost(data, postId)) {
        pinned.posts.delete(postId);
      } else {
        data = mergePostIntoHomePages(data, post);
        repaired = true;
      }
    }
    if (pinned.posts.size === 0) state.queries.delete(event.query.queryHash);
    if (repaired) {
      queryClient.setQueryData(event.query.queryKey, data);
      void queryClient.invalidateQueries({
        queryKey: event.query.queryKey,
        exact: true,
        refetchType: "none",
      });
    }
    if (state.queries.size === 0) {
      state.unsubscribe();
      confirmedHomePinStates.delete(queryClient);
    }
  });
  confirmedHomePinStates.set(queryClient, state);
  return state;
}

export function pinConfirmedHomePost(
  queryClient: QueryClient,
  query: Query,
  post: PostResponse,
): void {
  const pinState = ensureHomePinState(queryClient);
  const pinned = pinState.queries.get(query.queryHash);
  if (pinned?.query === query) {
    pinned.posts.delete(post.post_id);
    pinned.posts.set(post.post_id, post);
  } else {
    pinState.queries.set(query.queryHash, {
      query,
      posts: new Map([[post.post_id, post]]),
    });
  }
}

export function replaceConfirmedHomePostPin(
  queryClient: QueryClient,
  post: PostResponse,
): void {
  for (const pinned of confirmedHomePinStates
    .get(queryClient)
    ?.queries.values() ?? []) {
    if (pinned.posts.has(post.post_id)) pinned.posts.set(post.post_id, post);
  }
}

export function removeConfirmedHomePostPin(
  queryClient: QueryClient,
  postId: string,
): void {
  const state = confirmedHomePinStates.get(queryClient);
  if (!state) return;
  for (const [queryHash, pinned] of state.queries) {
    pinned.posts.delete(postId);
    if (pinned.posts.size === 0) state.queries.delete(queryHash);
  }
  if (state.queries.size === 0) {
    state.unsubscribe();
    confirmedHomePinStates.delete(queryClient);
  }
}

export function resetConfirmedHomePins(queryClient: QueryClient): void {
  const state = confirmedHomePinStates.get(queryClient);
  if (!state) return;
  state.unsubscribe();
  state.queries.clear();
  confirmedHomePinStates.delete(queryClient);
}

/** Reconcile a canonical Home read without dropping projection-lagged writes. */
export function reconcileConfirmedHomePins(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  data: InfiniteData<HomeFeedPage>,
): InfiniteData<HomeFeedPage> {
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  if (!query) return data;
  const state = confirmedHomePinStates.get(queryClient);
  const pinned = state?.queries.get(query.queryHash);
  if (!pinned || pinned.query !== query) return data;

  let reconciled = data;
  for (const [postId, post] of pinned.posts) {
    if (homeContainsPost(reconciled, postId)) pinned.posts.delete(postId);
    else reconciled = mergePostIntoHomePages(reconciled, post) ?? reconciled;
  }
  if (pinned.posts.size === 0) state?.queries.delete(query.queryHash);
  if (state?.queries.size === 0) {
    state.unsubscribe();
    confirmedHomePinStates.delete(queryClient);
  }
  return reconciled;
}
